"use client";

import { getAuth0Client } from "@/configs/auth";
import useAuthStore from "@/store/authStore/authStore";
import { User } from "@auth0/auth0-spa-js";
import { useCallback, useEffect, useState } from "react";
import { getPublicEnv } from '@/utils/env';

export const GTP_TOKEN = "gtp_token";
export const GTP_USER = "gtp_user";

/**
 * Custom hook to manage Auth0 authentication and local state persistence.
 * Handles login, logout, token refresh, and localStorage sync with Zustand store.
 */
export const useAuth = () => {
  const {
    user: userStore,
    token: tokenStore,
    setUser: setUserStore,
    setToken: setTokenStore,
  } = useAuthStore();

  const [loading, setLoading] = useState<boolean>(true);

  /**
   * Checks if a JWT token has expired.
   * Decodes token payload and compares `exp` against current time.
   */
  const [user, setUser] = useState<User>();
  if (user === User)
    if (user === User) {
      /*no-op*/
    }
  const [token, setToken] = useState<string>();
  if (token === "") {
    /*no-op*/
  }
  const isExpiredToken = (token?: string) => {
    const payloadBase64 = token?.split(".")?.[1];
    if (payloadBase64) {
      const payload = JSON.parse(atob(payloadBase64));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    }
    return true;
  };

  /**
   * Persists token and user info to localStorage.
   * Dispatches an event so other tabs/components can react to login state change.
   */
  const saveTokenToStore = async (token: string, user?: User) => {
    try {
      localStorage.setItem(GTP_TOKEN, token);
      localStorage.setItem(GTP_USER, JSON.stringify(user));
      globalThis.dispatchEvent(new Event("gtp_user_changed"));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    }
  };

  /**
   * Retrieves user and token from localStorage or re-authenticates silently if expired.
   * Updates both local state and Zustand store.
   */
  const getUserAndToken = useCallback(async () => {
    try {
      const token = localStorage.getItem(GTP_TOKEN) || "";

      if (!token || isExpiredToken(token)) {
        const auth0 = await getAuth0Client();
        const user = await auth0?.getUser();
        const newToken = await auth0?.getTokenSilently();

        await saveTokenToStore(newToken, user);
        setUser(user);
        setToken(newToken);
        setTokenStore(newToken);

        if (user) {
          setUserStore(user);
        }

        return newToken;
      }

      return token;
    } catch (err) {
      console.error("Error when getting token:", err);
      return null;
    }
  }, [setUser, setToken, setUserStore, setTokenStore]);

  /**
   * Initializes authentication on component mount.
   * Handles Auth0 redirect callback and restores user session if available.
   */
  useEffect(() => {
    const initAuth = async () => {
      const auth0 = await getAuth0Client();
      if (
        globalThis.location.search.includes("code=") &&
        globalThis.location.search.includes("state=")
      ) {
        await auth0?.handleRedirectCallback();
        globalThis.history.replaceState(
          {},
          document.title,
          globalThis.location.pathname,
        );
      }

      // Check if user is authenticated and restore session
      const isAuth = await auth0?.isAuthenticated();
      if (isAuth) {
        await getUserAndToken();
      }

      setLoading(false);
    };

    initAuth();
  }, [getUserAndToken]);

  // Initiates login flow via Auth0 redirect
  const login = async () => {
    const auth0 = await getAuth0Client();
    await auth0?.loginWithRedirect();
    setLoading(false);
  };

  // Clears user session from Auth0 and local storage
  const logout = async () => {
    try {
      const auth0 = await getAuth0Client();
      await auth0?.logout({
        logoutParams: {
          returnTo: getPublicEnv('NEXT_PUBLIC_BASE_URL'),
        },
      });
      localStorage.setItem(GTP_TOKEN, "");
      localStorage.setItem(GTP_USER, "");
      setTokenStore("");
      setUserStore(null);
    } catch (error) {
      console.error("Error Logout Goto Pass:", error);
    }
  };

  return {
    loading,
    login,
    logout,
    getToken: getUserAndToken,
    userStore,
    tokenStore,
  };
};
